import { useCallback, useEffect, useRef, useState } from 'react';
import DeckSlideStatic from './DeckSlideStatic';
import type { SlideDeck } from '@/lib/slideDeck';

interface DeckViewerProps {
  deck: SlideDeck;
  title?: string;
}

/**
 * Consultation d'une présentation partagée : navigation, plein écran et mode
 * présentateur. Aucune modification n'est possible.
 */
export default function DeckViewer({ deck, title = 'Présentation' }: DeckViewerProps) {
  const [index, setIndex] = useState(0);
  const [present, setPresent] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const slide = deck.slides[index];
  const total = deck.slides.length;

  const go = useCallback(
    (dir: -1 | 1) => setIndex((i) => Math.min(total - 1, Math.max(0, i + dir))),
    [total],
  );

  const enterFullscreen = async () => {
    const el = stageRef.current;
    if (!el) return;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
    } catch {
      /* plein écran refusé par le navigateur */
    }
    setPresent(true);
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch {
      /* ignore */
    }
    setPresent(false);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'Escape' && present) {
        void exitFullscreen();
      } else if (e.key.toLowerCase() === 'f') {
        void enterFullscreen();
      }
    };
    const onFsChange = () => {
      if (!document.fullscreenElement) setPresent(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, [go, present]);

  if (!slide) return null;

  return (
    <div className="space-y-4">
      <div
        ref={stageRef}
        className={present ? 'flex flex-col justify-center bg-foreground-950 p-4 md:p-8 min-h-screen' : ''}
      >
        <div className="relative">
          <div key={slide.id} className="animate-fade-in">
            <DeckSlideStatic slide={slide} theme={deck.theme} interactive />
          </div>
          <button
            type="button"
            onClick={() => (index > 0 ? go(-1) : undefined)}
            disabled={index === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 text-foreground-800 flex items-center justify-center disabled:opacity-30 cursor-pointer hover:bg-white"
            title="Diapositive précédente"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button
            type="button"
            onClick={() => (index < total - 1 ? go(1) : undefined)}
            disabled={index === total - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 text-foreground-800 flex items-center justify-center disabled:opacity-30 cursor-pointer hover:bg-white"
            title="Diapositive suivante"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>

        {present && showNotes && (
          <p className="mt-4 text-xs text-background-50/80 max-w-4xl mx-auto whitespace-pre-wrap">
            {slide.notes || 'Aucune note pour cette diapositive.'}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={index === 0}
          className="px-3.5 py-2 rounded-full border border-background-200/70 bg-background-50 text-foreground-700 text-xs font-medium disabled:opacity-40 cursor-pointer hover:bg-background-100 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-1"></i>Précédent
        </button>
        <span className="px-3 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-full">
          {index + 1} / {total}
        </span>
        <button
          type="button"
          onClick={() => go(1)}
          disabled={index === total - 1}
          className="px-3.5 py-2 rounded-full border border-background-200/70 bg-background-50 text-foreground-700 text-xs font-medium disabled:opacity-40 cursor-pointer hover:bg-background-100 whitespace-nowrap"
        >
          Suivant<i className="ri-arrow-right-line ml-1"></i>
        </button>
        <button
          type="button"
          onClick={() => void enterFullscreen()}
          className="px-3.5 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-fullscreen-line mr-1"></i>
          Plein écran
        </button>
        <button
          type="button"
          onClick={() => setShowNotes((v) => !v)}
          className={`px-3.5 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${
            showNotes ? 'bg-accent-100 text-accent-800' : 'border border-background-200/70 bg-background-50 text-foreground-700 hover:bg-background-100'
          }`}
        >
          <i className="ri-sticky-note-line mr-1"></i>
          Notes
        </button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        {deck.slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`relative rounded-md overflow-hidden border cursor-pointer transition-colors ${
              i === index ? 'border-primary-500 ring-1 ring-primary-300' : 'border-background-200/70 hover:border-background-300'
            }`}
            title={s.title || `Diapositive ${i + 1}`}
          >
            <DeckSlideStatic slide={s} theme={deck.theme} />
            <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px]">{i + 1}</span>
          </button>
        ))}
      </div>

      <p className="text-center text-[11px] text-foreground-400">
        {title} · consultation seule · utilisez les flèches ou F pour le plein écran
      </p>
    </div>
  );
}